import { useCallback, useEffect, useRef, useState } from 'react';
import { getDisplay } from '../../pages/MyProperties/listingDisplay';

function UploadIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
    );
}

function AssignPopover({ listings, onSelect, onClose, flipAbove }) {
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            onClose();
        }
    };

    return (
        <div
            ref={ref}
            className={`bulk-photos-assign-popover${flipAbove ? ' flip-above' : ''}`}
            role="listbox"
            onKeyDown={handleKeyDown}
        >
            {listings.map((listing) => (
                <button
                    key={listing._id}
                    type="button"
                    className="bulk-photos-assign-option"
                    role="option"
                    onClick={() => onSelect(listing._id)}
                >
                    {getDisplay(listing).name}
                </button>
            ))}
        </div>
    );
}

function TrayThumb({
    photo,
    isUploading,
    listings,
    onDragStart,
    onAssignToListing,
}) {
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [flipAbove, setFlipAbove] = useState(false);
    const thumbRef = useRef(null);
    const canDrag = !isUploading && photo.url && photo.cloudflareId;

    const openPopover = useCallback((e) => {
        e.stopPropagation();
        if (!canDrag || !listings.length) return;
        const rect = thumbRef.current?.getBoundingClientRect();
        if (rect) {
            setFlipAbove(rect.bottom + 210 > window.innerHeight);
        }
        setPopoverOpen(true);
    }, [canDrag, listings.length]);

    const handleAssign = useCallback((listingId) => {
        onAssignToListing(listingId, photo.id);
        setPopoverOpen(false);
    }, [onAssignToListing, photo.id]);

    return (
        <div
            ref={thumbRef}
            className="bulk-photos-thumb"
            draggable={canDrag}
            onDragStart={(e) => onDragStart(e, photo)}
        >
            {photo.url ? (
                <img src={photo.url} alt="" draggable={false} />
            ) : (
                <div className="bulk-photos-thumb-img">Uploading…</div>
            )}
            {canDrag && listings.length > 0 && (
                <>
                    <button
                        type="button"
                        className="bulk-photos-thumb-add"
                        aria-label={`Assign ${photo.label || 'photo'} to a property`}
                        onClick={openPopover}
                    >
                        +
                    </button>
                    {popoverOpen && (
                        <AssignPopover
                            listings={listings}
                            onSelect={handleAssign}
                            onClose={() => setPopoverOpen(false)}
                            flipAbove={flipAbove}
                        />
                    )}
                </>
            )}
        </div>
    );
}

export default function Tray({
    tray,
    listings,
    onDropFiles,
    onDragStart,
    onAssignToListing,
    uploadingIds,
}) {
    const inputRef = useRef(null);

    const handleDrop = (e) => {
        e.preventDefault();
        onDropFiles(Array.from(e.dataTransfer.files ?? []));
    };

    return (
        <div className="bulk-photos-tray">
            <div className="bulk-photos-tray-label">Uploaded photos · drag onto a property</div>
            <div
                className="bulk-photos-drop"
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        inputRef.current?.click();
                    }
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
            >
                <UploadIcon />
                Drop more photos here
            </div>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                    onDropFiles(Array.from(e.target.files ?? []));
                    e.target.value = '';
                }}
            />
            <div className="bulk-photos-thumbs">
                {tray.map((photo) => (
                    <TrayThumb
                        key={photo.id}
                        photo={photo}
                        isUploading={uploadingIds.has(photo.id)}
                        listings={listings}
                        onDragStart={onDragStart}
                        onAssignToListing={onAssignToListing}
                    />
                ))}
            </div>
        </div>
    );
}
